import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Box, Button, IconButton, Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  IReport,
  IReportStatus,
  IUnit,
} from "../../services/dashboard.services";
import { AppDispatch, IAppState } from "../../helpers/store";
import { downloadReport, generateReport } from "./reportsThunks";
import {
  CommunityTypes,
  ReportStatusTypes,
  ReportTypes,
} from "../../helpers/constants";
import { dataPaths, listenToDocument, withField } from "../../helpers/firebase";
import { useTranslation } from "react-i18next";
import { setReports, setSelectedReport } from "./reportsSlice";
import { getMonth } from "../../helpers/utils";
import Icons from "../../helpers/iconImports";
import RecommendationDialog from "./RecommendationDialog";
import RecommendationWarningDialog from "./RecommendationWarningDialog";
import { useHistory } from "react-router-dom";
import { Views } from "../../helpers/constants";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    reportItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      border: "1px solid",
      borderRadius: "4px",
      height: "35px",
      marginBottom: "10px",
      borderColor: theme.palette.light.main,
    },
    buttonBox: {
      borderLeft: "1px solid",
      height: "35px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "120px",
      borderColor: theme.palette.light.main,
    },
    submitButton: {
      width: "120px",
      height: "35px",
      "&:hover": {
        backgroundColor: "inherit",
        textDecoration: "inherit",
      },
    },
    buttonText: {
      fontWeight: "bold",
    },
    openText: {
      color: theme.palette.primary.main,
      fontWeight: "bold",
    },
    dateText: {
      paddingLeft: "8px",
    },
    buttonGroup: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      height: "100%",
    },
    recommendationButtonBox: {
      borderLeft: "1px solid",
      height: "35px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "40px",
      borderColor: theme.palette.light.main,
    },
    recommendationIcon: {
      width: "80%",
      height: "80%",
    },
  })
);

export function openReport(
  report: IReport,
  recommendationText?: string,
  communityName?: string
) {}

export function refreshReportList(
  reports: IReport[],
  messageData: { unit_id: string; report_type: string; report_date: string }
): IReport[] {
  return reports.filter(
    (report) =>
      !(
        report.unit_id === messageData.unit_id &&
        report.report_type === messageData.report_type &&
        report.report_date === messageData.report_date
      )
  );
}

export function ReportItem(props: IProps) {
  const { date, selectedUnit } = props;
  /* Hooks */
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const history = useHistory();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  const userLanguage = useSelector(
    (state: IAppState) => state.headerState.user?.language
  );

  const community = useSelector(
    (state: IAppState) => state.headerState.selectedCommunity
  );

  const isAipCommunity = community?.community_type === CommunityTypes.aip;

  // We're only using the first day of the month for report lookup
  const reportDate = `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-01`;
  const reportPath = dataPaths.report(
    selectedUnit.community_id ?? "",
    selectedUnit.id,
    ReportTypes.ACTIVITY,
    reportDate
  );
  const recommendationPath = dataPaths.reportRecommendation(
    selectedUnit.community_id ?? "",
    selectedUnit.id,
    ReportTypes.ACTIVITY,
    reportDate
  );

  const report = useSelector((state: IAppState) =>
    state.reportsState.reports?.find(
      (report) =>
        report.unit_id === selectedUnit.id && report.report_date === reportDate
    )
  );

  const reports = useSelector((state: IAppState) => state.reportsState.reports);
  const firebaseAuth = useSelector(
    (state: IAppState) => state.loginState.firebaseAuth
  );
  const [buttonText, setButtonText] = useState<string>(t("create"));
  const [reportStatus, setReportStatus] = useState(
    ReportStatusTypes.NOT_CREATED
  );

  const [reportRecommendation, setReportRecommendation] = useState("");
  const [timeUpdated, setTimeUpdated] = useState("");
  const [version, setVersion] = useState("");

  const updateButtonText = (reportStatus: IReportStatus) => {
    if (
      !reportStatus ||
      reportStatus.status === ReportStatusTypes.NOT_CREATED
    ) {
      setButtonText(t("create"));
    } else if (reportStatus.status === ReportStatusTypes.QUEUED) {
      setButtonText(t("queued"));
    } else if (reportStatus.status === ReportStatusTypes.STARTED) {
      setButtonText(t("started"));
    } else if (reportStatus.status === ReportStatusTypes.COMPLETED) {
      setButtonText(t("open"));
    } else if (reportStatus.status === ReportStatusTypes.NO_DATA) {
      setButtonText(t("no_data"));
    } else if (reportStatus.status === ReportStatusTypes.ERROR) {
      setButtonText(t("retry"));
    }
  };

  const viewPrintable = (
    report: IReport,
    recommendationText?: string,
    communityName?: string
  ) => {
    history.push(Views.REPORTS_PRINTABLE, {
      report,
      recommendationText,
      communityName,
    });
  };

  /* ---- Effects ---- */
  useEffect(() => {
    if (firebaseAuth && selectedUnit.community_id) {
      return listenToDocument(reportPath, (reportStatus) => {
        if (reportStatus) {
          setReportStatus(reportStatus.status);
          updateButtonText(reportStatus);
        } else {
          setReportStatus(ReportStatusTypes.NOT_CREATED);
          updateButtonText(reportStatus);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, firebaseAuth]);

  /* ---- Effects ---- */
  useEffect(() => {
    if (firebaseAuth && selectedUnit.community_id && !isAipCommunity) {
      return listenToDocument(
        withField(recommendationPath, "description"),
        (recommendation) => setReportRecommendation(recommendation || "")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, firebaseAuth, isAipCommunity]);

  useEffect(() => {
    if (firebaseAuth && selectedUnit.community_id && !isAipCommunity) {
      return listenToDocument(
        withField(recommendationPath, "time_updated"),
        (time_updated) => setTimeUpdated(time_updated || "")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, firebaseAuth, isAipCommunity]);

  useEffect(() => {
    if (firebaseAuth && selectedUnit.community_id && !isAipCommunity) {
      return listenToDocument(withField(reportPath, "version"), (version) =>
        setVersion(version || "")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, firebaseAuth, isAipCommunity]);

  const defaultStartDate = new Date();
  defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

  const handleButtonClick = async (date: Date) => {
    if (selectedUnit && selectedUnit.community_id) {
      if (!report || reportStatus !== ReportStatusTypes.COMPLETED) {
        const messageData = {
          report_type: ReportTypes.ACTIVITY,
          report_date: reportDate,
          community_id: selectedUnit.community_id,
          unit_id: selectedUnit.id,
        };
        if (
          reportStatus === ReportStatusTypes.NOT_CREATED ||
          reportStatus === ReportStatusTypes.ERROR
        ) {
          if (reports) {
            const filtered = refreshReportList(reports, messageData);
            dispatch(setReports(filtered));
          }
          dispatch(generateReport(messageData));
        } else if (reportStatus === ReportStatusTypes.COMPLETED) {
          let communityName = community?.name;
          const actionResult = await dispatch(downloadReport(messageData));

          if (downloadReport.fulfilled.match(actionResult)) {
            const downloadedReport = actionResult.payload;
            viewPrintable(
              downloadedReport,
              reportRecommendation,
              communityName
            );
          }
        }
      } else if (reportStatus === ReportStatusTypes.COMPLETED) {
        dispatch(setSelectedReport(report));
        viewPrintable(report, reportRecommendation, community?.name);
      }
    }
  };

  const openRecommendationDialog = () => {
    if (reportStatus === ReportStatusTypes.COMPLETED && version === "") {
      setWarningDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const closeRecommendationDialog = () => {
    setDialogOpen(false);
  };

  const closeWarningDialog = () => {
    setWarningDialogOpen(false);
  };

  const isDisabled =
    reportStatus === ReportStatusTypes.STARTED ||
    reportStatus === ReportStatusTypes.QUEUED ||
    reportStatus === ReportStatusTypes.NO_DATA;

  return (
    <Box className={classes.reportItem}>
      <Typography className={classes.dateText}>
        {date.getFullYear() + " " + getMonth(date.getMonth(), userLanguage)}
      </Typography>
      <Box className={classes.buttonGroup}>
        {!isAipCommunity && (
          <Box className={classes.recommendationButtonBox}>
            <IconButton
              aria-label="open_recommendation"
              onClick={openRecommendationDialog}
            >
              <img
                src={
                  reportRecommendation
                    ? Icons.EditRecommendationIcon
                    : Icons.NewRecommendationIcon
                }
                alt={
                  reportRecommendation
                    ? "edit_recommendation"
                    : "new_recommendation"
                }
                draggable={false}
                className={classes.recommendationIcon}
              />
            </IconButton>
          </Box>
        )}
        <Box className={classes.buttonBox}>
          <Button
            className={classes.submitButton}
            onClick={() => handleButtonClick(date)}
            disabled={isDisabled}
          >
            <Typography
              className={`${
                reportStatus === ReportStatusTypes.COMPLETED
                  ? classes.openText
                  : classes.buttonText
              }`}
            >
              {buttonText}
            </Typography>
          </Button>
        </Box>
      </Box>
      {!isAipCommunity && (
        <>
          <RecommendationDialog
            toggle={dialogOpen}
            unit_id={selectedUnit.id}
            reportDate={date}
            userLanguage={userLanguage}
            reportRecommendation={reportRecommendation}
            timeUpdated={timeUpdated}
            community_id={selectedUnit.community_id}
            onClose={closeRecommendationDialog}
            reportStatus={reportStatus}
            version={version}
            setButtonText={setButtonText}
          />
          <RecommendationWarningDialog
            toggle={warningDialogOpen}
            unit_id={selectedUnit.id}
            reportDate={date}
            userLanguage={userLanguage}
            onClose={closeWarningDialog}
            setDialogOpen={setDialogOpen}
          />
        </>
      )}
    </Box>
  );
}

interface IProps {
  date: Date;
  selectedUnit: IUnit;
}
